import { Repository, User } from 'src/components/types';
import { LoadingStatus } from 'src/constants/loading-status';

import {
  DevActivityKind,
  IssueCreationFailureReason,
  IssueCategory,
  IssueMigrationCurrentStep,
} from './constants';

export type Site = {
  cloudId: string;
  cloudName: string;
  cloudUrl: string;
  connected: boolean;
};

type AvatarUrls = {
  '16x16': string;
  '24x24': string;
  '32x32': string;
  '48x48': string;
  '128x128'?: string;
};

export type AvailableSite = {
  cloudId: string;
  displayName: string;
  url: string;
  products: Array<string>;
  adminAccess: boolean;
};

export type ProjectId = {
  cloudId: string;
  projectId: string;
};

export type Project = {
  avatarUrls: AvatarUrls;
  id: string;
  key: string;
  name: string;
  site: Site;
  url: string;
};

export type ProjectAssociation = {
  repository: Repository;
  project: Project;
  included: boolean;
  activity: boolean;
  associatedBy?: User;
};

export type CreateJiraIssuePayload = {
  cloudId: string;
  issueTypeId: number;
  projectId: string;
  summary: string;
};

export type CreateJiraIssueSagaPayload = CreateJiraIssuePayload & {
  projectKey: string;
  projectName: string;
};

export type CreatePrCommentIssuePayload = CreateJiraIssuePayload & {
  commentId: number;
  type: 'PrCommentIssue' | 'PrIssue';
};

export type SetFormErrorStatePayload = {
  failureReason: IssueCreationFailureReason;
  status: LoadingStatus;
};

export type SetPrFormErrorStatePayload = SetFormErrorStatePayload & {
  commentId: number;
};

export type IssueType = {
  id: number;
  name: string;
  subtask: boolean;
  iconUrl: string;
  fields?: {
    [fieldKey: string]: {
      name: string;
      required: boolean;
    };
  };
};

export type JiraIssue = {
  id: string;
  key: string;
  site: Site;
  project: Project;
  issueType: JiraIssueType;
  summary: string;
  updated?: string;
  status?: JiraIssueStatus;
  assignee?: JiraUser;
};

export type JiraIssueStatus = {
  name: string;
  category: IssueCategory;
};

export type JiraIssueType = {
  id: string;
  name: string;
  iconUrl: string;
};

export type PrCommentJiraIssue = {
  commentId: number;
  pullRequestId: number;
  repositoryUuid: string;
  type: string;
  issue: JiraIssue;
};

export type JiraUser = {
  accountId: string;
  displayName: string;
  avatarUrls: AvatarUrls;
};

export type DevActivity = {
  dataType: DevActivityKind;
  count: number;
  open?: boolean;
  state?: 'MERGED' | 'OPEN' | 'DECLINED';
};

export type DevActivityMap = {
  [cloudId: string]: {
    [issueId: string]: DevActivity;
  };
};

export type StatusCategory = {
  key: IssueCategory;
};

export type IssueStatus = {
  id: string;
  name: string;
  statusCategory: StatusCategory;
};

export type IssueTransition = {
  id: string;
  name: string;
  to: IssueStatus | undefined;
  hasScreen: boolean;
  isGlobal: boolean;
  isInitial: boolean;
  isAvailable: boolean;
  isConditional: boolean;
  triggers: string[];
};

export type IssueTransitionFormRowData = {
  selectedIssue: PrCommentJiraIssue;
  availableIssueTransitions: IssueTransition[];
  availableIssueTransitionsFetchedStatus: LoadingStatus;
  automaticTransition: IssueTransition | undefined;
  selectedTransition: IssueTransition | undefined;
  shouldTransition: boolean;
  hasUnsupportedTransitions: boolean;
};

export type FilterState = {
  siteCloudId: string;
  projectIds: string[];
  issueCategories: IssueCategory[];
  textFilterQuery: string;
  assignees: string[];
  sort: Sort;
  currentPage: number;
};

export type FilterStatePartial = Partial<FilterState>;

export type ChangeFilterStateAction = {
  type: string;
  payload: FilterStatePartial;
};

export type Assignee = {
  accountId: string;
  displayName: string;
  avatarUrls: AvatarUrls;
};

export enum SORT_ORDER {
  ASC = 'ASC',
  DESC = 'DESC',
}

export type Sort = {
  field: 'key' | 'status' | 'assignee' | 'updated';
  order: SORT_ORDER;
};

export type IssueMigrationToJiraProgress = {
  percentComplete: number;
  hasLog: boolean;
  currentStep: IssueMigrationCurrentStep;
  jiraProject: Project;
  warningsCount?: number;
  failuresCount?: number;
  totalIssuesToBeMigrated?: number;
  issuesMigrated?: number;
};

export type IssueMigrationToJiraStarted = {
  status: LoadingStatus;
  statusCode?: number;
};

export type IssueMigrationToJiraLogs = {
  status: LoadingStatus;
  logs?: string;
};

export enum AssigneeType {
  Self = 'SELF',
  Avatars = 'AVATARS',
  Unassigned = 'UNASSIGNED',
  Dropdown = 'DROPDOWN',
}

export type UserOption = {
  label: string;
  value: string;
  avatarUrl: string;
  avatar: JSX.Element;
};
